import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { Col, Row } from 'react-bootstrap';
import { RequestsFormValues } from '../../models';
import { Colors } from '../../../../../assets';
import { Spacing } from '../../../../../assets/GlobalStyle/Variables';
import { useProtocol } from '../../../context';

type SymptomKey =
  | 'temperature'
  | 'malaise'
  | 'runny_nose'
  | 'cough'
  | 'dyspnea'
  | 'headache'
  | 'chest_pain'
  | 'abdominal_pain'
  | 'muscle_pain'
  | 'vomiting'
  | 'diarrhea'
  | 'tonsillitis'
  | 'conjunctivitis'
  | 'tracheitis'
  | 'bronchitis'
  | 'exanthem'
  | 'loss_smell_taste'
  | 'other';

type SymptomConfig = {
  key: SymptomKey;
  label: string;
  hasValueC?: boolean;
  hasText?: boolean;
};

const SYMPTOMS: SymptomConfig[] = [
  { key: 'temperature', label: 'teplota', hasValueC: true },
  { key: 'malaise', label: 'malátnost' },
  { key: 'runny_nose', label: 'rýma' },
  { key: 'cough', label: 'kašel' },
  { key: 'dyspnea', label: 'dušnost' },
  { key: 'headache', label: 'bolest hlavy' },
  { key: 'chest_pain', label: 'bolest na hrudi' },
  { key: 'abdominal_pain', label: 'bolest břicha' },
  { key: 'muscle_pain', label: 'bolesti svalové' },
  { key: 'vomiting', label: 'zvracení' },
  { key: 'diarrhea', label: 'průjem' },
  { key: 'tonsillitis', label: 'tonsilitis' },
  { key: 'conjunctivitis', label: 'konjuktivitis' },
  { key: 'tracheitis', label: 'tracheitis' },
  { key: 'bronchitis', label: 'bronchitis' },
  { key: 'exanthem', label: 'exanthem' },
  { key: 'loss_smell_taste', label: 'ztráta čichu a chuti' },
  { key: 'other', label: 'jiné', hasText: true }
];

type SariKey =
  | 'treatment'
  | 'atb'
  | 'antivirals'
  | 'obesity'
  | 'respiratory_support'
  | 'ecmo'
  | 'pregnancy'
  | 'flu_vaccination';

type SariConfig = {
  key: SariKey;
  label: string;
  hasDate?: boolean;
  hasText?: boolean;
};

const SARI: SariConfig[] = [
  { key: 'treatment', label: 'Léčba / od data', hasDate: true, hasText: true },
  { key: 'atb', label: 'ATB' },
  { key: 'antivirals', label: 'Antivirotika' },
  { key: 'obesity', label: 'Obezita' },
  { key: 'respiratory_support', label: 'Podpora dýchání' },
  { key: 'ecmo', label: 'ECMO' },
  { key: 'pregnancy', label: 'těhotenství, trimestr', hasText: true },
  { key: 'flu_vaccination', label: 'očkování proti chřipce' }
];

type ExamKey =
  | 'influenza'
  | 'sarscov2'
  | 'rsv_metapneumo'
  | 'parainfl_corona'
  | 'rhino_entero'
  | 'adeno_boca'
  | 'parecho'
  | 'mers'
  | 'chlam_mycopl'
  | 'legionella'
  | 'other';

type ExamConfig = {
  key: ExamKey;
  label: string;
  hasText?: boolean;
};

const EXAMS: ExamConfig[] = [
  { key: 'influenza', label: 'INFL A/B/C nebo subtypy' },
  { key: 'sarscov2', label: 'SaRS-CoV-2 (COVID19)' },
  { key: 'rsv_metapneumo', label: 'RSV/metapneumov.' },
  { key: 'parainfl_corona', label: 'parainfl./koronaviry' },
  { key: 'rhino_entero', label: 'rhinoviry/enteroviry' },
  { key: 'adeno_boca', label: 'adeno/bocaviry' },
  { key: 'parecho', label: 'parechoviry' },
  { key: 'mers', label: 'MERS CoV' },
  { key: 'chlam_mycopl', label: 'Chlam./Mycopl.pn.' },
  { key: 'legionella', label: 'Legionella pn.' },
  { key: 'other', label: 'jiné', hasText: true }
];

type MaterialKey =
  | 'swab_nose_throat'
  | 'endotracheal_swab'
  | 'bal'
  | 'sputum_or_aspirate'
  | 'csf'
  | 'biopsy'
  | 'full_blood_clot'
  | 'saliva'
  | 'other';

type MaterialConfig = {
  key: MaterialKey;
  label: string;
  hasText?: boolean;
};

const MATERIALS: MaterialConfig[] = [
  { key: 'swab_nose_throat', label: 'Výtěr (nos+krk)' },
  { key: 'endotracheal_swab', label: 'Endotracheální stěr' },
  { key: 'bal', label: 'BAL' },
  { key: 'sputum_or_aspirate', label: 'Sputum/Endotracheální aspirát' },
  { key: 'csf', label: 'Likvor' },
  { key: 'biopsy', label: 'Biopsie' },
  { key: 'full_blood_clot', label: 'Krev plná srážlivá' },
  { key: 'saliva', label: 'Sliny' },
  { key: 'other', label: 'Jiný', hasText: true }
];

const p = (path: string) => `nrl_respiratory.${path}` as const;

export const NRLRespiratoryForm: FC = () => {
  const { register, watch, setValue, getValues } = useFormContext<RequestsFormValues>();
  const { protocol } = useProtocol();

  const handleMaterialFocus = (key: MaterialKey) => {
    const takenAtPath = p(`form_data.material.${key}.taken_at`) as any;
    const current = getValues(takenAtPath);
    if (!current && protocol?.autopsy_date) {
      setValue(takenAtPath, protocol.autopsy_date);
    }
  };

  const fluVaccination = watch(p('form_data.sari.flu_vaccination.enabled') as any);
  const symptomsOther = watch(p('form_data.symptoms.other.present') as any);

  return (
    <Wrapper>
      <Row>
        <Col xl={4} lg={12} md={12}>
          <PanelTitle>Příznaky</PanelTitle>
          <SymptomsGrid>
            <GridHead />
            <GridHead>°C</GridHead>
            <GridHead>dnů</GridHead>

            {SYMPTOMS.map((s) => (
              <SymptomRow key={s.key}>
                <SymptomLabel>{s.label}</SymptomLabel>

                {s.hasValueC ? (
                  <Input
                    type="number"
                    step="0.1"
                    placeholder=""
                    {...register(p(`form_data.symptoms.${s.key}.value_c`) as any)}
                  />
                ) : (
                  <Spacer />
                )}

                <DaysWrapper>
                  <input
                    type="checkbox"
                    {...register(p(`form_data.symptoms.${s.key}.present`) as any)}
                  />
                  <Input
                    type="number"
                    min="0"
                    placeholder=""
                    {...register(p(`form_data.symptoms.${s.key}.days`) as any)}
                  />
                </DaysWrapper>

                {s.hasText && symptomsOther && (
                  <OtherText
                    type="text"
                    placeholder="upřesnění"
                    {...register(p(`form_data.symptoms.${s.key}.text`) as any)}
                  />
                )}
              </SymptomRow>
            ))}
          </SymptomsGrid>

          <TextareaBlock>
            <label>Jiné důležité příznaky a epidemiologické souvislosti:</label>
            <textarea {...register(p('important_symptoms_note') as any)} />
          </TextareaBlock>

          <TextareaBlock>
            <label>Ohnisko:</label>
            <textarea {...register(p('focus_note') as any)} />
          </TextareaBlock>
        </Col>

        <Col xl={3} lg={12} md={12}>
          <PanelTitle>SARI</PanelTitle>

          <SariGrid>
            <SariHead />
            <SariHead>ano</SariHead>
            <SariHead>ne</SariHead>

            {SARI.map((item) => {
              const base = `form_data.sari.${item.key}`;
              return (
                <SariRow key={item.key}>
                  <SariLabel>{item.label}</SariLabel>

                  <input type="checkbox" {...register(p(`${base}.enabled`) as any)} />
                  <input type="checkbox" {...register(p(`${base}.disabled`) as any)} />

                  {item.hasDate && (
                    <InlineInputs>
                      <input type="date" {...register(p(`${base}.from_date`) as any)} />
                    </InlineInputs>
                  )}

                  {item.hasText && (
                    <InlineInputs>
                      <input type="text" {...register(p(`${base}.text`) as any)} />
                    </InlineInputs>
                  )}
                </SariRow>
              );
            })}

            <Divider />

            <SariRow>
              <SariLabel>název vakcíny</SariLabel>
              <Spacer />
              <Spacer />
              <InlineInputs>
                <input
                  type="text"
                  disabled={!fluVaccination}
                  {...register(p('form_data.sari.vaccine_name_text') as any)}
                />
              </InlineInputs>
            </SariRow>

            <SariRow>
              <SariLabel>šarže vakcíny</SariLabel>
              <Spacer />
              <Spacer />
              <InlineInputs>
                <input
                  type="text"
                  disabled={!fluVaccination}
                  {...register(p('form_data.sari.vaccine_batch_text') as any)}
                />
              </InlineInputs>
            </SariRow>
          </SariGrid>
        </Col>

        <Col xl={5} lg={12} md={12}>
          <PanelTitle>Vyšetření</PanelTitle>
          <ExamGrid>
            <ExamHead />
            <ExamHead>PCR</ExamHead>
            <ExamHead>Konfirmace</ExamHead>
            <ExamHead>{`Pozit. <28 pro NRL (sekv)`}</ExamHead>

            {EXAMS.map((e) => {
              const base = `form_data.examination.${e.key}`;
              return (
                <ExamRow key={e.key}>
                  <ExamLabel>{e.label}</ExamLabel>

                  <input type="checkbox" {...register(p(`${base}.pcr`) as any)} />
                  <input type="checkbox" {...register(p(`${base}.confirmation`) as any)} />
                  <input type="checkbox" {...register(p(`${base}.nrl_seq_lt28`) as any)} />

                  {e.hasText && (
                    <ExamOtherText
                      type="text"
                      placeholder="upřesnění"
                      {...register(p(`${base}.text`) as any)}
                    />
                  )}
                </ExamRow>
              );
            })}
          </ExamGrid>

          <PanelTitle style={{ marginTop: '16px' }}>Materiál</PanelTitle>
          <MaterialGrid>
            <MaterialHead />
            <MaterialHead>Datum odběru</MaterialHead>
            <MaterialHead>Poznámka</MaterialHead>

            {MATERIALS.map((m) => {
              const base = `form_data.material.${m.key}`;
              return (
                <MaterialRow key={m.key}>
                  <MaterialLabel>{m.label}</MaterialLabel>
                  <input type="date" {...register(p(`${base}.taken_at`) as any)} />
                  <input
                    type="text"
                    onFocus={() => handleMaterialFocus(m.key)}
                    {...register(p(`${base}.note`) as any)}
                  />
                  {m.hasText && (
                    <OtherMaterialText
                      type="text"
                      placeholder="upřesnění"
                      onFocus={() => handleMaterialFocus(m.key)}
                      {...register(p(`${base}.text`) as any)}
                    />
                  )}
                </MaterialRow>
              );
            })}
          </MaterialGrid>
        </Col>
      </Row>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing(3)};
`;

const PanelTitle = styled.div`
  font-weight: 700;
  margin-bottom: ${Spacing(2)};
`;

const SymptomsGrid = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 80px 140px;
  gap: ${Spacing(2)};
  align-items: center;
  margin-bottom: ${Spacing(3)};
`;

const GridHead = styled.div`
  color: ${Colors.grey};
  font-weight: 700;
`;

const SymptomRow = styled.div`
  display: contents;
`;

const SymptomLabel = styled.div`
  color: ${Colors.grey};
`;

const DaysWrapper = styled.div`
  display: grid;
  grid-template-columns: 22px 1fr;
  gap: ${Spacing(2)};
  align-items: center;

  input[type='checkbox'] {
    width: 18px;
    height: 18px;
  }
`;

const Input = styled.input`
  width: 100%;
  height: 32px;
  border: 1px solid ${Colors.lightGrey};
  padding: 0 ${Spacing(2)};
`;

const Spacer = styled.div``;

const OtherText = styled.input`
  grid-column: 1 / -1;
  height: 32px;
  border: 1px solid ${Colors.lightGrey};
  padding: 0 ${Spacing(2)};
  margin-bottom: ${Spacing(2)};
`;

const TextareaBlock = styled.div`
  margin-top: ${Spacing(3)};

  label {
    color: ${Colors.grey};
    font-weight: 700;
    margin-bottom: ${Spacing(2)};
    display: block;
  }

  textarea {
    width: 100%;
    min-height: 90px;
    border: 1px solid ${Colors.lightGrey};
    padding: ${Spacing(2)};
    resize: vertical;
  }
`;

const SariGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 42px 42px;
  gap: ${Spacing(2)};
  align-items: center;
`;

const SariHead = styled.div`
  color: ${Colors.grey};
  font-weight: 700;
`;

const SariRow = styled.div`
  display: contents;

  input[type='checkbox'] {
    width: 18px;
    height: 18px;
  }

  input[type='text'],
  input[type='date'] {
    height: 32px;
    border: 1px solid ${Colors.lightGrey};
    padding: 0 ${Spacing(2)};
    width: 100%;
  }
`;

const SariLabel = styled.div`
  color: ${Colors.grey};
`;

const InlineInputs = styled.div`
  grid-column: 1 / -1;
`;

const Divider = styled.hr`
  grid-column: 1 / -1;
  border: none;
  border-top: 1px solid ${Colors.lightGrey};
  margin: ${Spacing(2)} 0;
`;

const ExamGrid = styled.div`
  display: grid;
  grid-template-columns: 1.3fr 70px 100px 160px;
  gap: ${Spacing(2)};
  align-items: center;
`;

const ExamHead = styled.div`
  color: ${Colors.grey};
  font-weight: 700;
  font-size: 13px;
`;

const ExamRow = styled.div`
  display: contents;

  input[type='checkbox'] {
    width: 18px;
    height: 18px;
  }
`;

const ExamLabel = styled.div`
  color: ${Colors.grey};
`;

const ExamOtherText = styled.input`
  grid-column: 1 / -1;
  height: 32px;
  border: 1px solid ${Colors.lightGrey};
  padding: 0 ${Spacing(2)};
`;

const MaterialGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(200px, 260px) 180px 1fr;
  gap: ${Spacing(2)};
  align-items: start;

  input[type='date'],
  input[type='text'] {
    height: 32px;
    border: 1px solid ${Colors.lightGrey};
    padding: 0 ${Spacing(2)};
    width: 100%;
  }
`;

const MaterialHead = styled.div`
  color: ${Colors.grey};
  font-weight: 700;
`;

const MaterialRow = styled.div`
  display: contents;
`;

const MaterialLabel = styled.div`
  color: ${Colors.grey};
`;

const OtherMaterialText = styled.input`
  grid-column: 2 / -1;
  height: 32px;
  border: 1px solid ${Colors.lightGrey};
  padding: 0 ${Spacing(2)};
  width: 100%;
`;
